import React, {useCallback} from 'react';
import {
    Button,
    CheckBox,
    Dropdown,
    Flex,
    Text,
    Icon,
    PriorityIcon
} from '@priority-software/priority-style-react';

import {highlightText} from '../../../../../../_legacy/library/utils/highlightText';
import styles from '../../styles.module.scss';
import useSearchControl from './useSearchControl';
import {getLocalizedText, texts as constTexts} from '../../../../../../constants/TextConst';
import {SearchDropdown} from '../../../../../../_legacy/FormHeader/components/Search/SearchDropdown';
import List from '../../../../../../components/Common/List';
import {ISearchCondition} from '../../../../types/filters';
import {ISearchField} from '../../../../../../common/searchType';
import {Label} from '../../../../../../components/Common/Label';
import {helpPopoverRef} from '../../../../../../_shared/Help/constants';
import {APP_SEARCH_CURR_USER} from '../../../../consts';
import {createDataTestId} from '../../../../../../utils/dataTestId';

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    session: string;
    dropdownWidth?: number;
    scrollToBottom: () => void;
    onSelect: (value: string) => void;
    onRemoveValue: (value: string) => void;
    onClose?: () => void;
}

const SearchControl = (props: Props) => {
    const {
        options,
        requestParams,
        isOpen,
        isOldTriggerChoose,
        isSearchByName,
        setIsOpen,
        handlePatternChange,
        handleToggleOption,
        onScrollEnd,
        handleToggleCaseSensitive,
        handleToggleFromStart,
        isSelected,
        handleSearchByChange
    } = useSearchControl(props);

    const onSelectOption = (e, value: string) => {
        e.stopPropagation();
        handleToggleOption(value);
    };

    const placeholder = `${getLocalizedText(
        constTexts.searchSelectPlaceholder
    )} ${props.filter.title.toLowerCase()}`;

    const onOpenDropdown = value => {
        value && handlePatternChange(value);
        props.scrollToBottom();
        setIsOpen(true);
    };

    const handleClose = useCallback(() => {
        setIsOpen(false);
        props.onClose?.();
    }, []);
    const handlePageDown = useCallback(
        async (idx: number) => {
            const lastIndex = options.length - 1;
            let nextIndex = idx + 4;
            nextIndex = nextIndex > lastIndex ? lastIndex : nextIndex;
            return Promise.resolve({nextSelected: nextIndex});
        },
        [options.length]
    );
    const handlePageUp = useCallback((idx: number) => {
        let nextIndex = idx - 4;
        nextIndex = nextIndex >= 0 ? nextIndex : 0;
        return nextIndex;
    }, []);

    const isChoose = props.column.isChoose && !isOldTriggerChoose;
    return (
        <SearchDropdown
            open={isOpen}
            onOpen={onOpenDropdown}
            openingSide="leftSide"
            shouldHandleHomeAndEndPress
            handlePageDown={handlePageDown}
            handlePageUp={handlePageUp}
            inputProps={{
                value: requestParams.Pattern,
                placeholder,
                autoFocus: true,
                onChange: handlePatternChange
            }}
            onClose={handleClose}
            eventTypes={['touchstart', 'mousedown']}
            circularNavigation={false}
            isMultiSelect={true}
        >
            <div className="reactComp">
                <Dropdown.Content
                    width={!isChoose && props.dropdownWidth}
                    componentClasses={styles.fieldsList}
                >
                    {isOldTriggerChoose && (
                        <OldTriggerHeader
                            handleSearchByChange={handleSearchByChange}
                            isSearchByName={isSearchByName}
                        />
                    )}
                    <Dropdown.Section gradient>
                        {options?.length > 0 ? (
                            <List
                                onScrollToEnd={onScrollEnd}
                                autoHeight
                                autoHeightMax={200}
                                data={options}
                                renderItem={({item, index}) => (
                                    <Dropdown.Item
                                        divider={options && index !== options.length - 1}
                                        key={`${index} ${item.title}`}
                                        index={index}
                                        closeOnClick={false}
                                        isItemChecked={isSelected(item.value)}
                                        onClick={e => onSelectOption(e, item.value)}
                                    >
                                        {isChoose ? (
                                            <ChooseItem
                                                searchQuery={requestParams.Pattern}
                                                isSelected={isSelected(item.value)}
                                                onChange={e => onSelectOption(e, item.value)}
                                                option={item}
                                            />
                                        ) : (
                                            <Item
                                                searchQuery={requestParams.Pattern}
                                                isSelected={isSelected(item.value)}
                                                onChange={e => onSelectOption(e, item.value)}
                                                option={item}
                                            />
                                        )}
                                    </Dropdown.Item>
                                )}
                            />
                        ) : (
                            <>
                                <Dropdown.Item
                                    divider={true}
                                    key={requestParams.Pattern}
                                    index={0}
                                    closeOnClick={false}
                                    isItemChecked={isSelected(requestParams.Pattern)}
                                    onClick={e => onSelectOption(e, requestParams.Pattern)}
                                >
                                    <ChooseItem
                                        searchQuery={requestParams.Pattern}
                                        isSelected={isSelected(requestParams.Pattern)}
                                        onChange={e => onSelectOption(e, requestParams.Pattern)}
                                        option={{
                                            label: `${getLocalizedText(constTexts.search)} '${
                                                requestParams.Pattern
                                            }'`,
                                            value: props.filter.values[0]
                                        }}
                                    />
                                </Dropdown.Item>
                                <EmptyState />
                            </>
                        )}
                        <Footer
                            isChoose={isChoose}
                            onApply={() => setIsOpen(false)}
                            onToggleCaseSensitive={handleToggleCaseSensitive}
                            onToggleFromStart={handleToggleFromStart}
                            formStart={requestParams.FromStart}
                            ignoreCase={requestParams.IgnoreCase}
                        />
                    </Dropdown.Section>
                </Dropdown.Content>
            </div>
        </SearchDropdown>
    );
};

const EmptyState = () => (
    <Dropdown.Item closeOnClick={false}>
        <Text skin="subtitle-2" textColor="secondary-400">
            {getLocalizedText(constTexts.noSearchResults)}
        </Text>
    </Dropdown.Item>
);

const OldTriggerHeader = ({handleSearchByChange, isSearchByName}) => (
    <Flex componentClasses={styles.searchDropdownHeader}>
        <Dropdown.Header
            componentClasses={styles.searchDropdownTitle}
            title={`${getLocalizedText(constTexts.searchBy)} ${
                isSearchByName
                    ? getLocalizedText(constTexts.Name)
                    : getLocalizedText(constTexts.Number)
            }`}
        />
        <Label
            skin="paragraph-2"
            componentClasses={styles.underline}
            textColor="primary-500"
            onClick={handleSearchByChange}
        >
            {getLocalizedText(constTexts.changeSearchBy)}
        </Label>
    </Flex>
);

const showCurrentUserHelp = e => {
    e.stopPropagation();
    helpPopoverRef.current.showHelpPopover({
        content: getLocalizedText(constTexts.CurrentUserSearchHelp),
        anchor: e.target
    });
};

const Item = ({option, isSelected, searchQuery, onChange}) => (
    <CheckBox
        componentClasses={styles.searchItem}
        checked={isSelected}
        onChange={onChange}
        value={option.value}
        {...createDataTestId('filter-box-search-item', true)}
    >
        <Flex direction="row" componentClasses={styles.textWrapper}>
            <Flex justifyContent="flex-start" wrap={false} componentClasses={styles.descWrapper}>
                <Text
                    skin="paragraph-2"
                    componentClasses={styles.text}
                    textColor={
                        option.value === APP_SEARCH_CURR_USER ? 'primary-500' : 'primary-700'
                    }
                    {...createDataTestId('filter-box-option-description', true)}
                >
                    {highlightText(option.description, searchQuery, true, false, true)}
                </Text>
                {option.description2 && option.description && <div>{'\u2022'}</div>}
                {option.description2 && (
                    <Text
                        skin="paragraph-2"
                        componentClasses={styles.text}
                        textColor="primary-700"
                        {...createDataTestId('filter-box-option-description-2', true)}
                    >
                        {highlightText(option.description2, searchQuery, true, false, true)}
                    </Text>
                )}
                {option.value === APP_SEARCH_CURR_USER && (
                    <Icon icon={PriorityIcon.help} onClick={showCurrentUserHelp} />
                )}
            </Flex>
            <Text
                skin="paragraph-2"
                componentClasses={styles.text}
                textColor="secondary-400"
                {...createDataTestId('filter-box-option-label', true)}
            >
                {highlightText(option.label, searchQuery, true, false, true)}
            </Text>
        </Flex>
    </CheckBox>
);

const ChooseItem = ({option, isSelected, searchQuery, onChange}) => (
    <CheckBox
        componentClasses={styles.searchItem}
        checked={isSelected}
        onChange={onChange}
        value={option.value}
        {...createDataTestId('filter-box-choose-item', true)}
    >
        <Flex direction="row" componentClasses={styles.textWrapper}>
            {option.value !== APP_SEARCH_CURR_USER && (
                <Text
                    skin="paragraph-2"
                    componentClasses={styles.text}
                    textColor="primary-700"
                    {...createDataTestId('filter-box-option-label', true)}
                >
                    {highlightText(option.label, searchQuery, true, false, true)}
                </Text>
            )}
            <Flex justifyContent="flex-start" wrap={false} componentClasses={styles.descWrapper}>
                <Text
                    skin="paragraph-2"
                    componentClasses={styles.text}
                    textColor={
                        option.value === APP_SEARCH_CURR_USER ? 'primary-500' : 'primary-700'
                    }
                    {...createDataTestId('filter-box-option-description', true)}
                >
                    {highlightText(
                        option.description || option.description2,
                        searchQuery,
                        true,
                        false,
                        true
                    )}
                </Text>
                {option.value === APP_SEARCH_CURR_USER && (
                    <Icon icon={PriorityIcon.help} onClick={showCurrentUserHelp} />
                )}
            </Flex>
        </Flex>
    </CheckBox>
);

const Footer = ({
    ignoreCase,
    onToggleCaseSensitive,
    formStart,
    onToggleFromStart,
    onApply,
    isChoose
}) => (
    <Flex
        justifyContent={isChoose ? 'flex-end' : 'space-between'}
        componentClasses={styles.searchDropdownFooter}
        {...createDataTestId('filter-box-search-control-footer', true)}
    >
        {!isChoose && (
            <Flex componentClasses={styles.searchDropdownFooterBoxes} justifyContent="flex-start">
                <CheckBox
                    componentClasses={styles.headerCheckbox}
                    checked={ignoreCase}
                    onChange={onToggleCaseSensitive}
                >
                    <Text
                        componentClasses={styles.label}
                        textColor="secondary-400"
                        skin="caption-2"
                    >
                        {getLocalizedText(constTexts.ignorecase)}
                    </Text>
                </CheckBox>

                <CheckBox
                    componentClasses={styles.headerCheckbox}
                    checked={formStart}
                    onChange={onToggleFromStart}
                >
                    <Text
                        componentClasses={styles.label}
                        textColor="secondary-400"
                        skin="caption-2"
                    >
                        {getLocalizedText(constTexts.searchFromBegining)}
                    </Text>
                </CheckBox>
            </Flex>
        )}
        <Button width="regularFixed" onClick={onApply}>
            {getLocalizedText(constTexts.ok)}
        </Button>
    </Flex>
);

export default SearchControl;
